import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { X, ArrowRight, ArrowLeft, Sparkles, MapPinned, FileCheck2, Send, Package, Search, Radar, Wallet, Users, ClipboardPaste, RadioTower, BellRing, MousePointerClick } from "lucide-react";

const STEPS = {
  courier: [
    { icon: Sparkles, t: "Welcome aboard, carrier", d: "Quick tour of your side of CourierRadar — where the work is, how you win it, and how you get paid. Under a minute, promise." },
    { icon: Search, t: "Find loads here", d: "This is your live load board. Filter by route, vehicle, service or date — every card shows distance, weight and payout up front.", path: "/courier", target: "board-filters" },
    { icon: Wallet, t: "Bids, earnings, invoices", d: "Your pending bids and earnings live here. Delivered jobs become invoices automatically — you confirm when the money lands.", path: "/courier", target: "earnings-tab" },
    { icon: BellRing, t: "Jobs come to you", d: "Loads matching your vehicle and coverage land in your notifications the moment they're posted. Click one and you go straight to that job.", target: "notifications-bell" },
    { icon: Radar, t: "Ask Radar to do it", d: "This glowing button is Radar, your AI copilot. Tell it: “find van jobs in Germany and bid on the best one”, “accept that fixed-price load”, “email me the jobs report every morning”. It acts — you confirm.", target: "radar-ai-button" },
    { icon: FileCheck2, t: "One last thing", d: "Keep your documents current under Account — expired documents pause your ability to win work. That's it, go get your first load." },
  ],
  company: [
    { icon: Sparkles, t: "Welcome aboard, shipper", d: "Quick tour of your side of CourierRadar — post work in a minute, watch it move, keep one tidy invoice trail." },
    { icon: Package, t: "Post a job here", d: "The booking wizard walks you through stops, vehicle, timing and price. Save regular addresses for one-click repeats.", path: "/company", target: "new-job-btn" },
    { icon: Users, t: "Bids land here", d: "Approved carriers quote with price, rating and pickup time — compare and assign the best in one tap, right here.", path: "/company", target: "bids-to-decide" },
    { icon: MousePointerClick, t: "Everything lives in this menu", d: "Jobs, drivers, invoices, team, address book — the menu stays put while the right side changes.", path: "/company", target: "company-sidebar" },
    { icon: Radar, t: "Ask Radar to do it", d: "This glowing button is Radar, your AI copilot. Tell it: “book a van Berlin → Paris, Friday 8am”, “publish this order email as a job”, “compare couriers on CR-… and assign the best”. It acts — you confirm.", target: "radar-ai-button" },
    { icon: MapPinned, t: "One last thing", d: "Every active job appears on your live map with chat and signed POD built in. That's it — post your first load." },
  ],
  admin: [
    { icon: RadioTower, t: "Welcome to the Control Tower", d: "Your private operations view — exception-first, so what needs attention is always on top." },
    { icon: MousePointerClick, t: "Every queue lives here", d: "Approvals, documents, jobs, finances, marketing, integrations — the menu stays fixed while the right side scrolls.", path: "/control", target: "ct-sidebar" },
    { icon: Radar, t: "Radar works the queues too", d: "Ask who needs approval, which documents expire, which payments failed — or tell it to email you the Control Tower briefing every morning.", target: "radar-ai-button" },
  ],
};

const CARD_W = 380;
const CARD_H = 300;

export default function TourGuide() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role;
  const key = role ? `cr_tour_v2_${role}` : null;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });

  const finish = useCallback(() => {
    try {
      if (key) localStorage.setItem(key, "done");
      localStorage.removeItem("cr_tour_pending");
    } catch { /* ignore */ }
    setOpen(false);
    setStep(0);
  }, [key]);

  // Auto-open only right after account creation (flag set on register), once per role.
  useEffect(() => {
    if (!user || !role) { setOpen(false); return; }
    let pending = false, seen = true;
    try {
      pending = localStorage.getItem("cr_tour_pending") === role;
      seen = localStorage.getItem(key) === "done";
    } catch { /* private mode */ }
    if (pending && !seen) {
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [user, role, key]);

  useEffect(() => {
    const reopen = () => { setStep(0); setOpen(true); };
    window.addEventListener("cr-open-tour", reopen);
    return () => window.removeEventListener("cr-open-tour", reopen);
  }, []);

  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const steps = STEPS[role] || [];
  const current = steps[step];

  // Navigate to the step's page, then locate the target element and point at it.
  useEffect(() => {
    if (!open || !current) return;
    if (current.path && location.pathname !== current.path) navigate(current.path);
    setRect(null);
    if (!current.target) return;
    let tries = 0;
    const finder = setInterval(() => {
      const el = document.querySelector(`[data-testid="${current.target}"]`);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "instant" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        clearInterval(finder);
      } else if (++tries > 20) {
        clearInterval(finder);
      }
    }, 150);
    return () => clearInterval(finder);
  }, [open, step]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !user || !current) return null;
  const last = step === steps.length - 1;
  const Icon = current.icon;
  const pad = 8;

  // Card placement: near the target with an arrow, centered when there's no target.
  let cardStyle = { left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  let arrow = null;
  if (rect) {
    const below = rect.top + rect.height + CARD_H + 40 < vp.h;
    const cx = Math.min(Math.max(rect.left + rect.width / 2, CARD_W / 2 + 12), vp.w - CARD_W / 2 - 12);
    const top = below ? rect.top + rect.height + pad + 14 : rect.top - CARD_H - pad - 14;
    cardStyle = { left: cx, top: Math.max(top, 12), transform: "translateX(-50%)", width: CARD_W, maxWidth: "calc(100vw - 24px)" };
    const ax = Math.min(Math.max(rect.left + rect.width / 2 - cx, -CARD_W / 2 + 28), CARD_W / 2 - 28);
    arrow = (
      <span className={`absolute w-4 h-4 bg-white rotate-45 ${below ? "-top-2" : "-bottom-2"}`}
        style={{ left: `calc(50% + ${ax}px)`, marginLeft: -8 }} />
    );
  }

  return (
    <div className="fixed inset-0 z-[70]" data-testid="tour-overlay" role="dialog" aria-modal="true" aria-label="Platform tour">
      {rect ? (
        <>
          {/* blurred dim with a clear hole around the target */}
          <div className="absolute bg-[#1C1B1A]/45 backdrop-blur-md" style={{ top: 0, left: 0, right: 0, height: Math.max(rect.top - pad, 0) }} />
          <div className="absolute bg-[#1C1B1A]/45 backdrop-blur-md" style={{ top: rect.top + rect.height + pad, left: 0, right: 0, bottom: 0 }} />
          <div className="absolute bg-[#1C1B1A]/45 backdrop-blur-md" style={{ top: rect.top - pad, left: 0, width: Math.max(rect.left - pad, 0), height: rect.height + pad * 2 }} />
          <div className="absolute bg-[#1C1B1A]/45 backdrop-blur-md" style={{ top: rect.top - pad, left: rect.left + rect.width + pad, right: 0, height: rect.height + pad * 2 }} />
          <div className="absolute rounded-2xl border-2 border-[#F2D06B] pointer-events-none animate-pulse"
            style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} data-testid="tour-spotlight" />
        </>
      ) : (
        <div className="absolute inset-0 bg-[#1C1B1A]/45 backdrop-blur-md" />
      )}
      <div className="absolute bg-white rounded-[28px] shadow-[0_30px_80px_rgba(28,27,26,0.35)]" style={cardStyle} data-testid="tour-card">
        {arrow}
        <div className="flex items-center justify-between px-5 pt-4">
          <p className="cr-label !text-[#A09D95]" data-testid="tour-progress">Step {step + 1} of {steps.length}</p>
          <button data-testid="tour-skip" onClick={finish}
            className="flex items-center gap-1 text-[11px] font-semibold text-[#A09D95] hover:text-[#1C1B1A] transition-colors">
            Skip tour <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="px-6 pt-4 pb-5 text-center">
          <span className="w-12 h-12 rounded-full bg-[#1C1B1A] flex items-center justify-center mx-auto mb-3">
            <Icon className="w-6 h-6 text-[#F2D06B]" />
          </span>
          <h2 className="font-heading text-base font-semibold tracking-tight mb-1.5" data-testid="tour-step-title">{current.t}</h2>
          <p className="text-xs text-[#78756E] leading-relaxed" data-testid="tour-step-body">{current.d}</p>
        </div>
        <div className="px-6 pb-5">
          <div className="flex justify-center gap-1.5 mb-4">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#1C1B1A]" : "w-1.5 bg-[#E8E4D9]"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <button data-testid="tour-back" onClick={() => setStep(step - 1)}
                className="cr-btn-outline !px-4 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
            )}
            <button data-testid="tour-next" onClick={() => (last ? finish() : setStep(step + 1))}
              className="cr-btn-dark flex-1 !py-3 flex items-center justify-center gap-1.5">
              {last ? "Start exploring" : "Next"} {!last && <ArrowRight className="w-3.5 h-3.5" />}
            </button>
          </div>
          {!last && <p className="text-[10px] text-[#A09D95] text-center mt-2">Recommended — it takes under a minute. Replay anytime from the footer.</p>}
        </div>
      </div>
    </div>
  );
}
